/**
 * Descriptor template registry.
 * Maps each {@link DescriptorVariant} to its template asset filename and a
 * human-readable friendly name, and resolves the registry entry for a
 * programme's descriptor variant.
 * @module export/descriptor-template-registry
 */

import type { Programme } from "@/types";

import type { DescriptorVariant } from "./descriptor-variant";
import { resolveDescriptorVariant } from "./descriptor-variant";

/** Registry entry describing a descriptor template asset. */
export interface DescriptorTemplateEntry {
  /** Machine filename of the template asset under `public/assets`. */
  fileName: string;
  /** Human-readable name for the template. */
  friendlyName: string;
  /**
   * QQI-published version/designation of the template asset, so an export is
   * attributable to the source template it was generated from (e.g. the HET
   * template is the February 2026 revision, not the superseded April 2024 one).
   */
  templateVersion: string;
  sourceFileName: string;
  sourceSha256: string;
  contentSha256: string;
}

/**
 * Descriptor template assets keyed by variant.
 *
 * Uses a single, consistent, scalable filename convention
 * (`descriptor-template-<variant>.docx`) so future variants extend cleanly.
 */
export const DESCRIPTOR_TEMPLATES: Record<DescriptorVariant, DescriptorTemplateEntry> = {
  "non-cas": {
    fileName: "descriptor-template-non-cas.docx",
    friendlyName: "Higher Education Programme Descriptor",
    templateVersion: "February 2026",
    sourceFileName: "het-programme-descriptor-template (4).docx",
    sourceSha256: "c08e7ff07a735255b2efb27c8b1af60c01509ac8634be0b0bfdde80016342fb7",
    contentSha256: "a237efaeb1506a00f5637a18d3c35a4f99cca9602ea0be47aa5b0729add5dc9a",
  },
  cas: {
    fileName: "descriptor-template-cas.docx",
    friendlyName: "QQI FET/CAS Programme Descriptor",
    templateVersion: "April 2024",
    sourceFileName: "fet-cas-programme-descriptor-template-april-2024 (1).docx",
    sourceSha256: "80a3f4b10f49609198fcb7acfa86911aad70d84f007eea2c933e4ca4a838e814",
    contentSha256: "9f1ab797a5d2e0598b4e73a88b9054c4eabcba9f34a418bcc51cc6bd6c6c7631",
  },
};

/**
 * Resolves the descriptor template entry for a programme, keyed by its
 * resolved descriptor variant ({@link resolveDescriptorVariant}).
 */
export function resolveDescriptorTemplate(programme: Programme): DescriptorTemplateEntry {
  return DESCRIPTOR_TEMPLATES[resolveDescriptorVariant(programme)];
}
